import {
  BooleanCheck,
  CheckResultRenderer,
  isBooleanCheckFailed,
} from '@backstage-community/plugin-tech-insights-react';
import { MarkdownContent } from '@backstage/core-components';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';

type CheckResult = Parameters<CheckResultRenderer['component']>[0];

/**
 * The type the tech-insights JSON rules engine registers its checks under.
 * Hardcoded upstream too, in the renderer this one replaces.
 */
const JSON_RULES_ENGINE = 'json-rules-engine';

/**
 * What a fact retriever reports as a fact's source when it could not read what
 * it needed. Kept in step with `READ_ERROR_SOURCE` in
 * packages/backend/src/plugins/tech-insights/repositoryReads.ts, which every
 * retriever that reads a repository shares.
 */
const READ_ERROR_SOURCE = 'error';

/**
 * The suffix on the fact a retriever reports that source under. Those facts are
 * `githubRepositorySource` and `pnpmVersionSource` today, and a retriever that
 * reads a repository is expected to follow the same naming.
 *
 * Matched on the name rather than on the value alone, so that a fact which
 * legitimately holds the string "error" does not silently blank out its checks.
 */
const SOURCE_FACT_SUFFIX = 'Source';

/** Shown in place of a check's own description when it could not be run. */
const UNDETERMINED_DESCRIPTION =
  'This check could not be run. Backstage could not read the repository, so whether it passes is unknown.';

/**
 * Whether the check ran against a value the backend could not determine.
 *
 * Exported because the scorecards page classifies entities by this third state
 * as well, and `CheckResultRenderer` has no way to report it.
 *
 * A retriever that cannot read a repository says so in the fact rather than
 * leaving the fact out. Leaving it out would take the entity off the scorecard,
 * which is how a check that does not apply is already reported. A rate limit
 * would then look like a repository the check never covered.
 */
export function isUndetermined(checkResult: CheckResult): boolean {
  return Object.entries(checkResult.facts).some(
    ([name, fact]) =>
      name.endsWith(SOURCE_FACT_SUFFIX) && fact.value === READ_ERROR_SOURCE,
  );
}

/**
 * Renders json-rules-engine results as pass, fail, or could not be determined.
 *
 * The third state is why this exists. Upstream renders every result as a
 * boolean. A check the backend could not evaluate then shows as a failure,
 * telling someone their repository is non-compliant when Backstage simply
 * could not read it.
 *
 * `isFailed` returns false for them, so nobody is told a check failed when it
 * never ran.
 *
 * Upstream's entity card scores them as passes, so the entity page uses
 * EntityScorecardCard instead, which scores over determined checks only.
 * The scorecards page in packages/app/src/components/techInsights already
 * counts the three states separately and is unaffected.
 */
export const atgCheckResultRenderer: CheckResultRenderer = {
  type: JSON_RULES_ENGINE,
  component: checkResult =>
    isUndetermined(checkResult) ? (
      <ReportProblemOutlinedIcon color="disabled" />
    ) : (
      <BooleanCheck checkResult={checkResult} />
    ),
  description: checkResult =>
    isUndetermined(checkResult) ? (
      <MarkdownContent content={UNDETERMINED_DESCRIPTION} />
    ) : (
      <MarkdownContent content={checkResult.check.description} />
    ),
  isFailed: checkResult =>
    isUndetermined(checkResult) ? false : isBooleanCheckFailed(checkResult),
};
